import { PageSkeleton } from "components/Skeletons/PageSkeleton";
import { TableSkeleton } from "components/Skeletons/TableSkeleton";
import { PageContent } from "components/styled";
import { Subheader } from "components/Subheader/Subheader";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { EManageHrTabView } from "library/types";
import { useCallback, useEffect } from "react";
import { selectedHrTabState } from "store";
import {
  expandedBenefitsAtom,
  expandedEmployeesAtom,
  onlyActiveImplementationsState,
  searchEmployeeFilterValueState,
  selectedBenefitsAtom,
  selectedCompanyState,
  selectedEmployeesAtom,
} from "store/UIHrPage";
import {
  expandedBenefitsAtom as expandedBenefitsDashboardAtom,
  selectedBenefitsAtom as selectedBenefitsDashboardAtom,
} from "store/UIHrPageDashboard";

import useEmployeeViewData from "hooks/useEmployeeViewData";
import useNeedsAttentionData from "hooks/useNeedsAttentionData";
import { ROUTES } from "library/constants";
import { useLocation } from "react-router-dom";
import { HrPageToolbar } from "./components/HrPageToolbar/HrPageToolbar";
import { ManagePageView } from "./components/ManagePageView/ManagePageView";
import { SummarySkeleton } from "./components/ManagePageView/SummaryView/SummarySkeleton";
import { useChatSessionId } from "./hooks/useChatSessionId";

export function HrPage() {
  useChatSessionId();
  const selectedCompany = useAtomValue(selectedCompanyState);
  const location = useLocation();
  const selectedTab = useAtomValue(selectedHrTabState);
  const onlyActiveImplementations = useAtomValue(
    onlyActiveImplementationsState
  );

  const setSelectedBenefits = useSetAtom(selectedBenefitsDashboardAtom);

  const [searchEmployeeFilterValue, setSearchEmployeeFilterValue] = useAtom(
    searchEmployeeFilterValueState
  );

  useEffect(() => {
    setSelectedBenefits(new Set([]));
  }, [selectedCompany]);

  const handleSearchEmployeeFilterValue = useCallback(
    (value) => {
      setSearchEmployeeFilterValue(value);
    },
    [setSearchEmployeeFilterValue]
  );

  const setSelectedItems = useSetAtom(selectedEmployeesAtom);
  const setExpandedItems = useSetAtom(expandedEmployeesAtom);
  const setSelectedPackages = useSetAtom(selectedBenefitsAtom);
  const setSelectedDashboardPackages = useSetAtom(
    selectedBenefitsDashboardAtom
  );
  const setExpandedPackages = useSetAtom(expandedBenefitsAtom);
  const setSelectedPackagesDashboard = useSetAtom(
    selectedBenefitsDashboardAtom
  );
  const setExpandedPackagesDashboard = useSetAtom(
    expandedBenefitsDashboardAtom
  );

  const { needsAttentionData } = useNeedsAttentionData({
    countryCode: selectedCompany?.countryCode || 0,
    clientInternalNumberGOS: selectedCompany?.clientInternalNumberGos || "0",
    enabled: true,
    searchEmployeeFilterValue: searchEmployeeFilterValue,
  });

  useEffect(() => {
    setSearchEmployeeFilterValue("");
    setSelectedItems(new Set<string>());
    setExpandedItems(new Set<string>());
    setSelectedPackages(new Set<string>());
    //setSelectedDashboardPackages(new Set<string>([]));
    setExpandedPackages(new Set<string>());
    setSelectedPackagesDashboard(new Set<string>());
    setExpandedPackagesDashboard(new Set<string>());
  }, [selectedCompany]);

  const Skeleton = (() => {
    if (selectedTab === EManageHrTabView.summary) {
      return <SummarySkeleton />;
    }
    if (selectedTab === EManageHrTabView.employees) {
      return <PageSkeleton isListView />;
    }
    if (selectedTab === EManageHrTabView.actionHistory) {
      return <TableSkeleton rowItems={10} />;
    }
    if (selectedTab === EManageHrTabView.benefits) {
      return <PageSkeleton isCardView />;
    }
  })();

  const { loadingEmployeeData, companyPackagesLoading, companyEmployees } =
    useEmployeeViewData({
      countryCode: selectedCompany?.countryCode || null,
      clientInternalNumberGOS: selectedCompany?.clientInternalNumberGos || "",
      enabled: !!selectedCompany,
      onlyActiveImplementations,
      searchEmployeeFilterValue,
      selectedTab,
    });

  if (loadingEmployeeData || companyPackagesLoading) return Skeleton;
  return (
    <>
      <Subheader
        searchFilterValue={searchEmployeeFilterValue}
        isHrPage
        showSearchFilter={selectedTab === EManageHrTabView.employees}
        items={companyEmployees || []}
        setSearchFilterValue={handleSearchEmployeeFilterValue}
        chatBotDisabled={!selectedCompany}
        showNeedsAttention={location.pathname.includes(ROUTES.HR)}
        needsAttentionCount={needsAttentionData?.length || 0}
      />
      {selectedCompany && <HrPageToolbar />}
      <PageContent>
        {selectedCompany && (
          <>
            <ManagePageView />
          </>
        )}
      </PageContent>
    </>
  );
}
